import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useBranding } from './hooks/useBranding';
import { useSip } from './hooks/useSip';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { EmergencyPage } from './pages/EmergencyPage';
import { ScreensaverController } from './components/ScreensaverController';
import { DimOverlay } from './components/DimOverlay';
import { SleepOverlay } from './components/SleepOverlay';
import { EmergencyButton } from './components/EmergencyButton';
import { PermissionGate } from './components/PermissionGate';
import { BatteryWarning } from './components/BatteryWarning';
import { WebSplash } from './components/WebSplash';
import { OnboardingWelcome } from './components/OnboardingWelcome';
import { useOnboarding } from './hooks/useOnboarding';
import { useMqttTelemetry } from './hooks/useMqttTelemetry';
import { CommandOverlay } from './components/CommandOverlay';
import './styles/global.css';

// Lazy-load pages that pull in heavy deps (sip.js ~150KB, camera APIs)
const ConnectivityPage = lazy(() => import('./pages/ConnectivityPage').then(m => ({ default: m.ConnectivityPage })));
const QRLoginPage = lazy(() => import('./pages/QRLoginPage').then(m => ({ default: m.QRLoginPage })));
const AdminSetupPage = lazy(() => import('./pages/AdminSetupPage').then(m => ({ default: m.AdminSetupPage })));
const InCallPage = lazy(() => import('./pages/InCallPage').then(m => ({ default: m.InCallPage })));
const IncomingCallPage = lazy(() => import('./pages/IncomingCallPage').then(m => ({ default: m.IncomingCallPage })));
const DoorbellPage = lazy(() => import('./pages/DoorbellPage').then(m => ({ default: m.DoorbellPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const TestModePage = lazy(() => import('./pages/TestModePage').then(m => ({ default: m.TestModePage })));
const ContactsPage = lazy(() => import('./pages/ContactsPage').then(m => ({ default: m.ContactsPage })));
const PhotosPage = lazy(() => import('./pages/PhotosPage').then(m => ({ default: m.PhotosPage })));
const InfoPage = lazy(() => import('./pages/InfoPage').then(m => ({ default: m.InfoPage })));
const VoicemailPage = lazy(() => import('./pages/VoicemailPage').then(m => ({ default: m.VoicemailPage })));
const CallHistoryPage = lazy(() => import('./pages/CallHistoryPage').then(m => ({ default: m.CallHistoryPage })));
const HomeScreenSettingsPage = lazy(() => import('./pages/HomeScreenSettingsPage').then(m => ({ default: m.HomeScreenSettingsPage })));
const DialerPage = lazy(() => import('./pages/DialerPage').then(m => ({ default: m.DialerPage })));
const GamesPage = lazy(() => import('./pages/GamesPage').then(m => ({ default: m.GamesPage })));
const MemoryGamePage = lazy(() => import('./pages/games/MemoryGamePage').then(m => ({ default: m.MemoryGamePage })));
const SudokuPage = lazy(() => import('./pages/games/SudokuPage').then(m => ({ default: m.SudokuPage })));
const WordSearchPage = lazy(() => import('./pages/games/WordSearchPage').then(m => ({ default: m.WordSearchPage })));
const TriviaPage = lazy(() => import('./pages/games/TriviaPage').then(m => ({ default: m.TriviaPage })));
const Game2048Page = lazy(() => import('./pages/games/Game2048Page').then(m => ({ default: m.Game2048Page })));
const SolitairePage = lazy(() => import('./pages/games/SolitairePage').then(m => ({ default: m.SolitairePage })));
const TicTacToePage = lazy(() => import('./pages/games/TicTacToePage').then(m => ({ default: m.TicTacToePage })));
const HangmanPage = lazy(() => import('./pages/games/HangmanPage').then(m => ({ default: m.HangmanPage })));
const SimonPage = lazy(() => import('./pages/games/SimonPage').then(m => ({ default: m.SimonPage })));
const ChessPage = lazy(() => import('./pages/games/ChessPage').then(m => ({ default: m.ChessPage })));
const CheckersPage = lazy(() => import('./pages/games/CheckersPage').then(m => ({ default: m.CheckersPage })));
const FindDifferencesPage = lazy(() => import('./pages/games/FindDifferencesPage').then(m => ({ default: m.FindDifferencesPage })));
const SlidingPuzzlePage = lazy(() => import('./pages/games/SlidingPuzzlePage').then(m => ({ default: m.SlidingPuzzlePage })));
const AgendaPage = lazy(() => import('./pages/AgendaPage').then(m => ({ default: m.AgendaPage })));
const DisplaySettingsPage = lazy(() => import('./pages/DisplaySettingsPage').then(m => ({ default: m.DisplaySettingsPage })));
const InAppBrowserPage = lazy(() => import('./pages/InAppBrowserPage').then(m => ({ default: m.InAppBrowserPage })));
const TodayPage = lazy(() => import('./pages/TodayPage').then(m => ({ default: m.TodayPage })));
const WeatherPage = lazy(() => import('./pages/WeatherPage').then(m => ({ default: m.WeatherPage })));
const NewsPage = lazy(() => import('./pages/NewsPage').then(m => ({ default: m.NewsPage })));

/**
 * Watches for incoming SIP calls and navigates to /incoming automatically.
 * Must be rendered inside BrowserRouter.
 */
function CallRouter() {
  const { currentCall } = useSip();
  const navigate = useNavigate();
  const location = useLocation();
  const prevCallRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentCall) {
      prevCallRef.current = null;
      return;
    }

    // Only auto-navigate for NEW incoming calls (not our own outgoing calls)
    if (
      currentCall.direction === 'inbound' &&
      currentCall.state === 'ringing_in' &&
      prevCallRef.current !== currentCall.id &&
      location.pathname !== '/incoming' &&
      location.pathname !== '/doorbell'
    ) {
      prevCallRef.current = currentCall.id;
      // Route videophone calls (7XXX extensions) to doorbell page
      const isVideophone = /^7\d{3}$/.test(currentCall.remoteParty);
      const target = isVideophone ? '/doorbell' : '/incoming';
      console.log('[CallRouter] Incoming call from', currentCall.remoteParty, `— navigating to ${target}`);
      navigate(target);
    }
  }, [currentCall, navigate, location.pathname]);

  return null;
}

/**
 * Global emergency button — shows on all authenticated pages.
 * Full bar on home page, compact icon on other pages.
 * Hidden on emergency page itself, login, and call pages.
 */
function GlobalEmergencyButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const hiddenPaths = ['/emergency', '/login', '/qr-login', '/admin-setup', '/call', '/incoming', '/doorbell'];
  if (hiddenPaths.includes(location.pathname)) return null;
  return <EmergencyButton onClick={() => navigate('/emergency')} />;
}

/** Activates MQTT telemetry + shows session conflict overlay */
function MqttTelemetryProvider() {
  const { sessionConflict } = useMqttTelemetry();

  if (sessionConflict) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}>
        <div style={{
          backgroundColor: '#fff',
          borderRadius: 16,
          padding: '32px 24px',
          maxWidth: 400,
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#128274;</div>
          <h2 style={{ margin: '0 0 12px', fontSize: 20, color: '#dc2626' }}>
            Session Ended
          </h2>
          <p style={{ margin: 0, fontSize: 16, color: '#555', lineHeight: 1.5 }}>
            You have been logged out because the same account was used to sign in on another device.
            SIP can only be active on one device at a time.
          </p>
          <p style={{ margin: '16px 0 0', fontSize: 14, color: '#999' }}>
            Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return null;
}

function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontSize: 'var(--font-body)',
      color: '#888',
    }}>Loading...
          </div>
  );
}

export function App() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { isCompleted: onboardingDone, completeOnboarding } = useOnboarding();
  const [splashDone, setSplashDone] = useState(false);

  // Apply building branding at the App root (T1.3 / Phase 1). Previously this
  // lived in HomePage and stripped CSS custom properties on unmount, which
  // caused branding to evaporate whenever the user navigated away from Home.
  // Hoisting it here makes the bridge run exactly once for the lifetime of
  // the React tree. Safe to call with undefined when unauthenticated — the
  // hook short-circuits via useApi(null).
  useBranding(user?.tenantId);

  // Gate 1: Web splash (always shows briefly on cold start)
  if (!splashDone) {
    return <WebSplash onComplete={() => setSplashDone(true)} />;
  }

  // Gate 2: Onboarding (first launch only)
  if (!onboardingDone) {
    return <OnboardingWelcome onComplete={completeOnboarding} />;
  }

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        fontSize: 'var(--font-heading)',
        color: '#666',
      }}>Loading...
              </div>
    );
  }

  return (
    <PermissionGate>
    <BrowserRouter>
      {isAuthenticated && <CallRouter />}
      {isAuthenticated && <GlobalEmergencyButton />}
      {isAuthenticated && <BatteryWarning />}
      {isAuthenticated && <MqttTelemetryProvider />}
      {isAuthenticated && <CommandOverlay />}
      {isAuthenticated && <ScreensaverController />}
      {isAuthenticated && <DimOverlay />}
      {isAuthenticated && <SleepOverlay />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes (no auth required) */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
          />
          <Route path="/qr-login" element={<QRLoginPage />} />
          <Route path="/admin-setup" element={<AdminSetupPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={isAuthenticated ? <HomePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/emergency"
            element={isAuthenticated ? <EmergencyPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/connectivity"
            element={isAuthenticated ? <ConnectivityPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/call"
            element={isAuthenticated ? <InCallPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/incoming"
            element={isAuthenticated ? <IncomingCallPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/doorbell"
            element={isAuthenticated ? <DoorbellPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/settings"
            element={isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/settings/home-screen"
            element={isAuthenticated ? <HomeScreenSettingsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/settings/display"
            element={isAuthenticated ? <DisplaySettingsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/test-mode"
            element={isAuthenticated ? <TestModePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/contacts"
            element={isAuthenticated ? <ContactsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/photos"
            element={isAuthenticated ? <PhotosPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/info"
            element={isAuthenticated ? <InfoPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/voicemail"
            element={isAuthenticated ? <VoicemailPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/call-history"
            element={isAuthenticated ? <CallHistoryPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/dialer"
            element={isAuthenticated ? <DialerPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games"
            element={isAuthenticated ? <GamesPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/memory"
            element={isAuthenticated ? <MemoryGamePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/sudoku"
            element={isAuthenticated ? <SudokuPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/word-search"
            element={isAuthenticated ? <WordSearchPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/trivia"
            element={isAuthenticated ? <TriviaPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/2048"
            element={isAuthenticated ? <Game2048Page /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/solitaire"
            element={isAuthenticated ? <SolitairePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/tic-tac-toe"
            element={isAuthenticated ? <TicTacToePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/hangman"
            element={isAuthenticated ? <HangmanPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/color-match"
            element={isAuthenticated ? <SimonPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/chess"
            element={isAuthenticated ? <ChessPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/checkers"
            element={isAuthenticated ? <CheckersPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/find-differences"
            element={isAuthenticated ? <FindDifferencesPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/sliding-puzzle"
            element={isAuthenticated ? <SlidingPuzzlePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/agenda"
            element={isAuthenticated ? <AgendaPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/browser"
            element={isAuthenticated ? <InAppBrowserPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/today"
            element={isAuthenticated ? <TodayPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/weather"
            element={isAuthenticated ? <WeatherPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/news"
            element={isAuthenticated ? <NewsPage /> : <Navigate to="/login" replace />}
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
    </PermissionGate>
  );
}
